/**
 * The customer portal — plan §6.11, §3.7's "customer portal" surface.
 *
 * One column, no nav, cards at 14px radius, one action per card, 15–16px type
 * — §3.7 describes this as the same shape as the pay page, because it is the
 * same reader: somebody on a phone who does not work here and will not be
 * trained on it.
 *
 * What is *not* here is the point. No status chips, no job numbers, no crew
 * names, no internal notes, no margins. §6.11: "nothing internal leaks". A
 * customer does not need to know a job is "Quoted" and is entitled not to see
 * what it cost to do.
 *
 * Billing appears only for a bill-to contact, and appears by being *absent*
 * otherwise — the API sends null rather than an empty list (§G.6), so there is
 * nothing here to draw an empty Billing card from.
 */
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Button, Card, EmptyState, SectionLabel, Tag } from '../components/primitives.js'
import { Field, TextAreaField } from '../components/fields.js'
import { Modal } from '../components/overlays.js'
import { RowtedLogo } from '../frame/logo.js'
import { formatDollars, formatListDate } from '../lib/format.js'
import { request } from '../lib/api.js'

interface Me {
  company: string
  contact: { name: string; email: string | null; account: string | null }
  properties: { id: string; name: string; address: string; isBillTo: boolean }[]
  permissions: Record<string, boolean>
}

interface Page {
  property: { name: string; address: string }
  waiting: { id: string; title: string; total: string; sentAt: string | null }[]
  visits: { id: string; scheduledDate: string; description: string; done: boolean }[]
  billing: {
    id: string; reference: string; issueDate: string; dueDate: string
    total: string; balance: string; status: string
  }[] | null
}

/** The frame: a logo, a name, and nothing that looks like an app. */
function PortalFrame({ me, children }: { me: Me | null; children: React.ReactNode }) {
  return (
    <div className="min-h-app bg-bg">
      <header className="flex h-[60px] items-center gap-3 border-b border-line bg-surface px-4">
        <RowtedLogo tenantName={me?.company ?? null} />
        <span className="ml-auto text-cell text-text-muted">{me?.contact.name}</span>
      </header>
      <main className="mx-auto max-w-[680px] px-4 py-4">{children}</main>
      <footer className="px-4 pb-8 pt-2 text-center text-sub text-text-faint">
        Powered by Rowted
      </footer>
    </div>
  )
}

/**
 * Where the magic link lands. Exchanges the token, then goes home — or to the
 * portal page the link was sent for (a proposal's link opens the proposal).
 */
export function PortalEnter() {
  const { token = '' } = useParams()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [error, setError] = useState<string | null>(null)
  /* Only a page of the portal itself, never somewhere else. */
  const wanted = params.get('next') ?? ''
  const next = /^\/portal\/[\w/-]+$/.test(wanted) ? wanted : '/portal'

  useEffect(() => {
    request(`/portal/enter/${token}`, { method: 'POST' })
      .then(() => navigate(next, { replace: true }))
      /*
       * A link works once. Opened again by somebody still signed in from the
       * first time, it still goes where it was sent for.
       */
      .catch((cause: unknown) => request('/portal/me')
        .then(() => navigate(next, { replace: true }))
        .catch(() => setError(cause instanceof Error ? cause.message : 'That link did not work.')))
  }, [token, navigate, next])

  return (
    <PortalFrame me={null}>
      {error
        ? (
          <EmptyState
            title="That link has expired"
            body="Links are good for seven days and can only be used once. Ask us for a new one and we will send it straight over."
          />
        )
        : <p className="m-0 text-body text-text-muted">Signing you in…</p>}
    </PortalFrame>
  )
}

/**
 * The portal with no portal session — the one screen a signed-out customer
 * meets, wherever they arrived: the home page, or a quote's own address
 * opened without its link (walkthrough Oct 30, §4 — that page said *Not
 * available · Sign in to the portal first*, a server error with nothing
 * after it). A customer signs in by the link we email; there is no password.
 */
export function PortalSignedOut() {
  return (
    <PortalFrame me={null}>
      <EmptyState
        title="You are signed out"
        body="Open the link we emailed you to come back in."
      />
    </PortalFrame>
  )
}

/** The list of places. One property goes straight through to its page. */
export function PortalHome() {
  const navigate = useNavigate()
  const [me, setMe] = useState<Me | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    request<Me>('/portal/me')
      .then((payload) => {
        setMe(payload)
        if (payload.properties.length === 1) {
          navigate(`/portal/p/${payload.properties[0]!.id}`, { replace: true })
        }
      })
      .catch(() => setError('signed-out'))
  }, [navigate])

  if (error) return <PortalSignedOut />

  return (
    <PortalFrame me={me}>
      <SectionLabel className="mb-2 block">Your places</SectionLabel>
      <div className="flex flex-col gap-2">
        {(me?.properties ?? []).map((property) => (
          <Card key={property.id} className="p-3">
            <button
              type="button"
              data-portal-property
              onClick={() => navigate(`/portal/p/${property.id}`)}
              className="flex w-full flex-col items-start text-left"
            >
              <span className="text-[16px] font-bold text-text">{property.name}</span>
              <span className="text-body text-text-muted">{property.address}</span>
            </button>
          </Card>
        ))}
      </div>
    </PortalFrame>
  )
}

/**
 * One property, in §6.11's order: what needs you → visits → recent work →
 * billing (if bill-to).
 */
export function PortalProperty() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [me, setMe] = useState<Me | null>(null)
  const [page, setPage] = useState<Page | null>(null)
  const [asking, setAsking] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const [who, payload] = await Promise.all([
        request<Me>('/portal/me'),
        request<Page>(`/portal/properties/${id}`),
      ])
      setMe(who)
      setPage(payload)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That did not load.')
    }
  }, [id])

  useEffect(() => { void load() }, [load])

  if (error) {
    return <PortalFrame me={me}><EmptyState title="Not available" body={error} /></PortalFrame>
  }
  if (!page) {
    return <PortalFrame me={me}><p className="m-0 text-body text-text-muted">Loading…</p></PortalFrame>
  }

  const upcoming = page.visits.filter((visit) => !visit.done)
  const done = page.visits.filter((visit) => visit.done)

  return (
    <PortalFrame me={me}>
      <div className="mb-3">
        <h1 className="m-0 text-[20px] font-bold text-text">{page.property.name}</h1>
        <p className="m-0 text-body text-text-muted">{page.property.address}</p>
      </div>

      {/* What needs you. First, because it is the only part with a deadline. */}
      {page.waiting.length > 0 && (
        <section className="mb-4">
          <SectionLabel className="mb-2 block">Needs you</SectionLabel>
          {page.waiting.map((quote) => (
            <Card key={quote.id} className="mb-2 rounded-[14px] p-3.5">
              <p className="m-0 text-[16px] font-bold text-text">{quote.title}</p>
              <p className="m-0 mt-0.5 text-body text-text-muted">
                A quote for {formatDollars(Number(quote.total))} is waiting for your go-ahead.
              </p>
              <Button
                variant="primary"
                className="mt-2.5 h-11 w-full"
                onClick={() => navigate(`/portal/quote/${quote.id}`)}
              >
                Look at the quote
              </Button>
            </Card>
          ))}
        </section>
      )}

      {upcoming.length > 0 && (
        <section className="mb-4">
          <SectionLabel className="mb-2 block">Coming up</SectionLabel>
          <Card className="rounded-[14px] p-0">
            <ul className="m-0 list-none p-0">
              {upcoming.map((visit) => (
                <li key={visit.id}
                  className="border-b border-line-soft px-3.5 py-3 last:border-b-0">
                  <p data-portal-date className="m-0 text-body font-semibold text-text">
                    {formatListDate(visit.scheduledDate)}
                  </p>
                  <p className="m-0 text-body text-text-muted">{visit.description}</p>
                </li>
              ))}
            </ul>
          </Card>
        </section>
      )}

      {done.length > 0 && (
        <section className="mb-4">
          <SectionLabel className="mb-2 block">Recent work</SectionLabel>
          <Card className="rounded-[14px] p-0">
            <ul className="m-0 list-none p-0">
              {done.slice(0, 8).map((visit) => (
                <li key={visit.id}
                  className="border-b border-line-soft px-3.5 py-3 last:border-b-0">
                  <p className="m-0 text-body text-text">{visit.description}</p>
                  <p data-portal-date className="m-0 text-sub text-text-muted">
                    {formatListDate(visit.scheduledDate)}
                  </p>
                </li>
              ))}
            </ul>
          </Card>
        </section>
      )}

      {/*
        * Billing, and only when the API sent it. §G.6: a contact who is not
        * bill-to gets `null` rather than an empty list, so there is nothing
        * here to draw a card from — no section, not an empty one.
        */}
      {page.billing !== null && (
        <section className="mb-4">
          <SectionLabel className="mb-2 block">Billing</SectionLabel>
          {page.billing.length === 0
            ? (
              <Card className="rounded-[14px] p-3.5">
                <p className="m-0 text-body text-text-muted">Nothing owing. Thank you.</p>
              </Card>
            )
            : (
              <Card className="rounded-[14px] p-0">
                <ul className="m-0 list-none p-0">
                  {page.billing.map((invoice) => (
                    <li key={invoice.id}
                      className="flex items-center gap-3 border-b border-line-soft px-3.5 py-3 last:border-b-0">
                      <div className="min-w-0 flex-1">
                        <p className="m-0 text-body font-semibold text-text">
                          {invoice.reference}
                        </p>
                        <p className="m-0 text-sub text-text-muted">
                          due <span data-portal-date>{formatListDate(invoice.dueDate)}</span>
                        </p>
                      </div>
                      <span className="tnum shrink-0 text-body text-text">
                        {formatDollars(Number(invoice.total))}
                      </span>
                      {Number(invoice.balance) > 0
                        ? <Tag tone="warn">{formatDollars(Number(invoice.balance))} owing</Tag>
                        : <Tag tone="ok">Paid</Tag>}
                    </li>
                  ))}
                </ul>
              </Card>
            )}
        </section>
      )}

      {me?.permissions.request_service && (
        <Button className="h-11 w-full" onClick={() => setAsking(true)}>
          Ask us for something
        </Button>
      )}

      <AskForWork
        open={asking}
        onClose={() => setAsking(false)}
        propertyId={id}
        onDone={() => { setAsking(false); void load() }}
      />
    </PortalFrame>
  )
}

function AskForWork({
  open, onClose, propertyId, onDone,
}: {
  open: boolean
  onClose: () => void
  propertyId: string
  onDone: () => void
}) {
  const [title, setTitle] = useState('')
  const [detail, setDetail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function send() {
    setBusy(true)
    setError(null)
    try {
      await request('/portal/requests', {
        method: 'POST',
        body: { propertyId, title: title.trim(), detail: detail.trim() },
      })
      setTitle('')
      setDetail('')
      onDone()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That did not send.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="What do you need?"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={busy || title.trim().length < 3}
            onClick={() => void send()}>
            {busy ? 'Sending…' : 'Send it'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error && <p className="m-0 text-body text-danger">{error}</p>}
        <Field
          label="In a few words"
          value={title}
          onChange={setTitle}
          placeholder="The hedge by the gate has gone wild"
          required
        />
        <TextAreaField
          label="Anything else we should know"
          value={detail}
          onChange={setDetail}
          placeholder="It is blocking the path to the bins."
        />
        <p className="m-0 text-sub text-text-muted">
          We will have a look and come back to you with a price before anything
          happens.
        </p>
      </div>
    </Modal>
  )
}
